import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';

// ─── Company Admin Dashboard ────────────────────────────────────────────────

interface CompanyDashboard {
  company_name: string;
  plan: string;
  plan_status: string;
  total_properties: number;
  properties_by_status: Record<string, number>;
  total_agents: number;
  total_views: number;
  total_favorites: number;
  unread_messages: number;
  unread_inquiries: number;
  unread_internal: number;
  recent_messages: Array<{
    id: number;
    sender_name: string;
    body_preview: string;
    message_type: string;
    created_at: string;
  }>;
  todays_visits: Array<{
    id: number;
    property_title: string;
    client_name: string;
    agent_name: string;
    scheduled_at: string;
    status: string;
  }>;
  upcoming_visits: Array<{
    id: number;
    property_title: string;
    client_name: string;
    agent_name: string;
    scheduled_at: string;
    status: string;
  }>;
  pending_visit_requests: number;
  recent_properties: Array<{
    id: number;
    title: string;
    status: string;
    slug: string;
    views_count: number;
    created_at: string;
  }>;
}

// ─── Translation Maps ────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  active: 'Activa',
  paused: 'Pausada',
  rented: 'Alquilada',
  sold: 'Vendida',
  archived: 'Archivada',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'badge-ghost',
  active: 'badge-success',
  paused: 'badge-warning',
  rented: 'badge-info',
  sold: 'badge-secondary',
  archived: 'badge-error',
};

const PLAN_LABELS: Record<string, string> = {
  basic: 'Básico',
  pro: 'Profesional',
  enterprise: 'Enterprise',
};

const PLAN_COLORS: Record<string, string> = {
  basic: 'badge-ghost',
  pro: 'badge-primary',
  enterprise: 'badge-accent',
};

const VISIT_STATUS_LABELS: Record<string, string> = {
  requested: 'Solicitada',
  confirmed: 'Confirmada',
  postponed: 'Postergada',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
};

const VISIT_STATUS_COLORS: Record<string, string> = {
  requested: 'badge-warning',
  confirmed: 'badge-info',
  postponed: 'badge-ghost',
  completed: 'badge-success',
  cancelled: 'badge-error',
  no_show: 'badge-error',
};

const MSG_TYPE_LABELS: Record<string, string> = {
  inquiry: 'Consulta',
  internal: 'Interno',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_BAR_COLORS: Record<string, string> = {
  draft: 'bg-neutral/40',
  active: 'bg-success',
  paused: 'bg-warning',
  rented: 'bg-info',
  sold: 'bg-secondary',
  archived: 'bg-error/60',
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `Hace ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return new Date(dateStr).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// ─── KPI Card Component ─────────────────────────────────────────────────────

function KpiCard({
  icon,
  value,
  label,
  borderColor,
  valueColor,
  subtitle,
  linkTo,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  borderColor: string;
  valueColor?: string;
  subtitle?: string;
  linkTo?: string;
}): React.ReactElement {
  const content = (
    <div
      className={`bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5 border-l-4 ${borderColor} transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${linkTo ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-base-content/50 uppercase tracking-wider">{label}</p>
          <p className={`text-3xl font-bold tabular-nums ${valueColor || 'text-base-content'}`}>{value.toLocaleString('es-AR')}</p>
          {subtitle && <p className="text-xs text-base-content/40">{subtitle}</p>}
        </div>
        <div className="text-2xl opacity-40">{icon}</div>
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}

// ─── Section Header Component ────────────────────────────────────────────────

function SectionHeader({
  title,
  linkTo,
  linkLabel = 'Ver todo',
}: {
  title: string;
  linkTo?: string;
  linkLabel?: string;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between pb-2 border-b border-base-content/5">
      <h2 className="text-base font-semibold text-base-content">{title}</h2>
      {linkTo && (
        <Link to={linkTo} className="text-xs font-medium text-primary hover:text-primary-focus transition-colors">
          {linkLabel} &rarr;
        </Link>
      )}
    </div>
  );
}

// ─── SVG Donut Component ─────────────────────────────────────────────────────

function MiniDonut({
  segments,
  size = 120,
}: {
  segments: Array<{ value: number; color: string; label: string }>;
  size?: number;
}): React.ReactElement {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <p className="text-xs text-base-content/30">Sin datos</p>
      </div>
    );
  }

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  let currentOffset = 0;

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width={size} height={size} viewBox="0 0 100 100" className="transform -rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="oklch(var(--bc)/0.05)" strokeWidth="12" />
        {segments.map((seg, i) => {
          const segLength = (seg.value / total) * circumference;
          const gap = segments.length > 1 ? 2 : 0;
          const dash = `${Math.max(segLength - gap, 0)} ${circumference - Math.max(segLength - gap, 0)}`;
          const offset = -currentOffset;
          currentOffset += segLength;
          return (
            <circle
              key={i}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              className={seg.color}
              stroke="currentColor"
              strokeWidth="12"
              strokeDasharray={dash}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          );
        })}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-base-content text-lg font-bold"
          transform="rotate(90 50 50)"
        >
          {total}
        </text>
      </svg>
      <div className="flex flex-wrap justify-center gap-3">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${seg.color} bg-current`} />
            <span className="text-xs text-base-content/60">
              {seg.label} ({seg.value})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Company Admin Dashboard ─────────────────────────────────────────────────

function CompanyAdminDashboard({ data }: { data: CompanyDashboard }): React.ReactElement {
  const totalByStatus = Object.values(data.properties_by_status).reduce((a, b) => a + b, 0);
  const maxStatusCount = Math.max(...Object.values(data.properties_by_status), 1);

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-base-content/40 uppercase tracking-widest">Panel de control</p>
          <h1 className="text-2xl font-bold mt-0.5">{data.company_name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <span className={`badge badge-sm ${PLAN_COLORS[data.plan] || 'badge-ghost'}`}>
              Plan {PLAN_LABELS[data.plan] || data.plan}
            </span>
            <span className="badge badge-sm badge-success badge-outline gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
              {data.plan_status === 'active' ? 'Activo' : data.plan_status}
            </span>
          </div>
        </div>
        <Link to="/app/properties/new" className="btn btn-primary btn-sm gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Nueva propiedad
        </Link>
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />
            </svg>
          }
          value={data.total_properties}
          label="Propiedades"
          borderColor="border-l-primary"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          value={data.total_agents}
          label="Agentes"
          borderColor="border-l-secondary"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
          value={data.total_views}
          label="Visitas web"
          borderColor="border-l-accent"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          }
          value={data.total_favorites}
          label="Favoritos"
          borderColor="border-l-warning"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
          value={data.unread_messages}
          label="Mensajes sin leer"
          borderColor="border-l-error"
          valueColor={data.unread_messages > 0 ? 'text-error' : undefined}
          subtitle={`${data.unread_inquiries} consultas · ${data.unread_internal} internos`}
          linkTo="/app/messages"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
          value={data.pending_visit_requests}
          label="Visitas pendientes"
          borderColor="border-l-info"
          valueColor={data.pending_visit_requests > 0 ? 'text-warning' : undefined}
          subtitle="por confirmar"
          linkTo="/app/visits"
        />
      </div>

      {/* ── Charts Row ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Properties by Status - horizontal bars */}
        <div className="lg:col-span-2 bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
          <SectionHeader title="Propiedades por estado" linkTo="/app/properties" linkLabel="Ver propiedades" />
          <div className="mt-4 space-y-3">
            {Object.entries(data.properties_by_status).map(([status, count]) => {
              const pct = totalByStatus > 0 ? (count / maxStatusCount) * 100 : 0;
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="text-xs w-20 text-right text-base-content/60 font-medium shrink-0">
                    {STATUS_LABELS[status] || status}
                  </span>
                  <div className="flex-1 h-7 bg-base-200/60 rounded-md overflow-hidden relative">
                    <div
                      className={`h-full rounded-md ${STATUS_BAR_COLORS[status] || 'bg-neutral/30'} transition-all duration-500 ease-out`}
                      style={{ width: `${pct}%`, minWidth: count > 0 ? '1.5rem' : '0' }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-base-content/70">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {totalByStatus > 0 && (
            <p className="text-xs text-base-content/30 mt-3 text-right">{totalByStatus} propiedades totales</p>
          )}
        </div>

        {/* Message Distribution Donut */}
        <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5 flex flex-col">
          <SectionHeader title="Distribución de mensajes" />
          <div className="flex-1 flex items-center justify-center py-4">
            <MiniDonut
              segments={[
                { value: data.unread_inquiries, color: 'text-warning', label: 'Consultas' },
                { value: data.unread_internal, color: 'text-info', label: 'Internos' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Quick Access Shortcuts ─────────────────────────────────────────── */}
      <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
        <SectionHeader title="Acceso rápido" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          {[
            {
              to: '/app/properties/new',
              label: 'Nueva propiedad',
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              ),
              color: 'text-primary',
            },
            {
              to: '/app/contracts',
              label: 'Nuevo contrato',
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
              color: 'text-secondary',
            },
            {
              to: '/app/messages',
              label: 'Ver mensajes',
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
              color: 'text-error',
            },
            {
              to: '/app/visits',
              label: 'Ver visitas',
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
              color: 'text-info',
            },
            {
              to: '/app/analytics',
              label: 'Analíticas',
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
              color: 'text-accent',
            },
            {
              to: '/app/settings',
              label: 'Configuración',
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
              color: 'text-base-content/60',
            },
          ].map((shortcut) => (
            <Link
              key={shortcut.to}
              to={shortcut.to}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-base-200/30 hover:bg-base-200/70 border border-transparent hover:border-base-content/5 transition-all duration-200 group"
            >
              <span className={`${shortcut.color} transition-transform duration-200 group-hover:scale-110`}>
                {shortcut.icon}
              </span>
              <span className="text-xs font-medium text-base-content/70 text-center leading-tight">{shortcut.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Agenda + Messages Row ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Agenda */}
        <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
          <SectionHeader title="Agenda de hoy" linkTo="/app/visits" linkLabel="Ver visitas" />
          {data.todays_visits.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-base-content/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">No hay visitas programadas para hoy</p>
            </div>
          ) : (
            <div className="mt-4 relative">
              {/* Timeline line */}
              <div className="absolute left-[3.25rem] top-2 bottom-2 w-px bg-base-content/10" />
              <div className="space-y-4">
                {data.todays_visits.map((visit) => (
                  <div key={visit.id} className="flex gap-4 items-start relative">
                    {/* Time */}
                    <div className="w-12 text-right shrink-0 pt-1">
                      <span className="text-sm font-mono font-semibold text-base-content/70">
                        {new Date(visit.scheduled_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {/* Dot */}
                    <div className="w-2.5 h-2.5 rounded-full bg-primary border-2 border-base-100 shrink-0 mt-2 relative z-10" />
                    {/* Card */}
                    <div className="flex-1 bg-base-200/40 rounded-lg p-3 hover:bg-base-200/70 transition-colors">
                      <p className="font-medium text-sm">{visit.property_title}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-base-content/50">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {visit.client_name}
                        </span>
                        <span className="text-xs text-base-content/40">|</span>
                        <span className="text-xs text-base-content/50">Agente: {visit.agent_name}</span>
                      </div>
                      <div className="mt-1.5">
                        <span className={`badge badge-xs ${VISIT_STATUS_COLORS[visit.status] || 'badge-ghost'}`}>
                          {VISIT_STATUS_LABELS[visit.status] || visit.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming this week */}
          {data.upcoming_visits.length > 0 && (
            <div className="mt-5 pt-4 border-t border-base-content/5">
              <p className="text-xs font-medium text-base-content/40 uppercase tracking-wider mb-3">Próximos 7 días</p>
              <div className="space-y-2">
                {data.upcoming_visits.slice(0, 5).map((visit) => (
                  <div key={visit.id} className="flex items-center justify-between py-1.5">
                    <div>
                      <p className="text-sm text-base-content/80">{visit.property_title}</p>
                      <p className="text-xs text-base-content/40">{visit.client_name}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-xs text-base-content/60">
                        {new Date(visit.scheduled_at).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-xs font-mono text-base-content/40">
                        {new Date(visit.scheduled_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent Messages */}
        <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
          <SectionHeader title="Mensajes recientes" linkTo="/app/messages" linkLabel="Ver todos" />
          {data.recent_messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-base-content/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm">No hay mensajes sin leer</p>
            </div>
          ) : (
            <div className="mt-4 space-y-1">
              {data.recent_messages.slice(0, 5).map((msg) => (
                <Link
                  key={msg.id}
                  to="/app/messages"
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-base-200/50 transition-colors group"
                >
                  {/* Avatar initials */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                    msg.message_type === 'inquiry'
                      ? 'bg-warning/15 text-warning'
                      : 'bg-info/15 text-info'
                  }`}>
                    {getInitials(msg.sender_name)}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{msg.sender_name}</span>
                      <span className={`badge badge-xs ${msg.message_type === 'inquiry' ? 'badge-warning' : 'badge-info'}`}>
                        {MSG_TYPE_LABELS[msg.message_type] || msg.message_type}
                      </span>
                    </div>
                    <p className="text-xs text-base-content/50 truncate mt-0.5">{msg.body_preview}</p>
                  </div>
                  {/* Time */}
                  <span className="text-xs text-base-content/30 whitespace-nowrap shrink-0 pt-0.5">
                    {msg.created_at ? timeAgo(msg.created_at) : ''}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Properties ──────────────────────────────────────────────── */}
      <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
        <SectionHeader title="Propiedades recientes" linkTo="/app/properties" linkLabel="Ver todas" />
        {data.recent_properties.length === 0 ? (
          <p className="text-sm text-base-content/30 py-6 text-center">No hay propiedades recientes</p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="table table-sm">
              <thead>
                <tr className="border-base-content/5">
                  <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider">Título</th>
                  <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider">Estado</th>
                  <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider text-right">Vistas</th>
                  <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider text-right">Fecha</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {data.recent_properties.map((prop) => (
                  <tr key={prop.id} className="hover border-base-content/5">
                    <td>
                      <Link to={`/app/properties/${prop.id}/edit`} className="font-medium text-sm hover:text-primary transition-colors">
                        {prop.title}
                      </Link>
                    </td>
                    <td>
                      <span className={`badge badge-sm ${STATUS_COLORS[prop.status] || 'badge-ghost'}`}>
                        {STATUS_LABELS[prop.status] || prop.status}
                      </span>
                    </td>
                    <td className="text-right tabular-nums text-sm text-base-content/60">{prop.views_count}</td>
                    <td className="text-right text-xs text-base-content/40">
                      {prop.created_at ? new Date(prop.created_at).toLocaleDateString('es-AR') : ''}
                    </td>
                    <td>
                      <Link to={`/app/properties/${prop.id}/edit`} className="btn btn-ghost btn-xs">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Root Dashboard ─────────────────────────────────────────────────────────

function RootDashboard({ data }: { data: Record<string, unknown> }): React.ReactElement {
  const d = data as {
    total_companies: number;
    active_companies: number;
    total_properties: number;
    total_users: number;
    recent_companies: Array<{ id: number; name: string; slug: string; plan: string; is_active: boolean }>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-medium text-base-content/40 uppercase tracking-widest">Administración</p>
        <h1 className="text-2xl font-bold mt-0.5">Panel de Control</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          value={d.total_companies}
          label="Empresas totales"
          borderColor="border-l-primary"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          value={d.active_companies}
          label="Empresas activas"
          borderColor="border-l-success"
          valueColor="text-success"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />
            </svg>
          }
          value={d.total_properties}
          label="Propiedades totales"
          borderColor="border-l-accent"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          value={d.total_users}
          label="Usuarios totales"
          borderColor="border-l-secondary"
        />
      </div>

      {/* Recent Companies */}
      <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
        <SectionHeader title="Empresas recientes" />
        <div className="overflow-x-auto mt-4">
          <table className="table table-sm">
            <thead>
              <tr className="border-base-content/5">
                <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider">Nombre</th>
                <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider">Plan</th>
                <th className="text-xs font-medium text-base-content/40 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody>
              {d.recent_companies?.map((c) => (
                <tr key={c.id} className="hover border-base-content/5">
                  <td className="font-medium text-sm">{c.name}</td>
                  <td>
                    <span className={`badge badge-sm ${PLAN_COLORS[c.plan] || 'badge-ghost'}`}>
                      {PLAN_LABELS[c.plan] || c.plan}
                    </span>
                  </td>
                  <td>
                    {c.is_active ? (
                      <span className="badge badge-sm badge-success badge-outline gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
                        Activa
                      </span>
                    ) : (
                      <span className="badge badge-sm badge-error badge-outline gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-error inline-block" />
                        Inactiva
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Agent Dashboard ────────────────────────────────────────────────────────

function AgentDashboard({ data }: { data: Record<string, unknown> }): React.ReactElement {
  const d = data as {
    assigned_properties: number;
    unread_messages: number;
    todays_visits: Array<{ id: number; property_title: string; client_name: string; scheduled_at: string; status: string }>;
    recent_messages: Array<{ id: number; sender_name?: string; body_preview: string; message_type: string; created_at: string }>;
  };

  const todaysCount = d.todays_visits?.length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-medium text-base-content/40 uppercase tracking-widest">Mi panel</p>
        <h1 className="text-2xl font-bold mt-0.5">Resumen del día</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />
            </svg>
          }
          value={d.assigned_properties}
          label="Propiedades asignadas"
          borderColor="border-l-primary"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
          value={d.unread_messages}
          label="Mensajes sin leer"
          borderColor="border-l-error"
          valueColor={d.unread_messages > 0 ? 'text-error' : undefined}
          linkTo="/app/messages"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
          value={todaysCount}
          label="Visitas hoy"
          borderColor="border-l-info"
          linkTo="/app/visits"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Visits */}
        <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
          <SectionHeader title="Visitas de hoy" linkTo="/app/visits" linkLabel="Ver todas" />
          {todaysCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-base-content/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">No hay visitas para hoy</p>
            </div>
          ) : (
            <div className="mt-4 relative">
              <div className="absolute left-[3.25rem] top-2 bottom-2 w-px bg-base-content/10" />
              <div className="space-y-4">
                {d.todays_visits.map((v) => (
                  <div key={v.id} className="flex gap-4 items-start relative">
                    <div className="w-12 text-right shrink-0 pt-1">
                      <span className="text-sm font-mono font-semibold text-base-content/70">
                        {new Date(v.scheduled_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="w-2.5 h-2.5 rounded-full bg-primary border-2 border-base-100 shrink-0 mt-2 relative z-10" />
                    <div className="flex-1 bg-base-200/40 rounded-lg p-3">
                      <p className="font-medium text-sm">{v.property_title}</p>
                      <p className="text-xs text-base-content/50 mt-0.5">{v.client_name}</p>
                      <span className={`badge badge-xs mt-1.5 ${VISIT_STATUS_COLORS[v.status] || 'badge-ghost'}`}>
                        {VISIT_STATUS_LABELS[v.status] || v.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent Messages */}
        <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
          <SectionHeader title="Mensajes recientes" linkTo="/app/messages" linkLabel="Ver todos" />
          {!d.recent_messages || d.recent_messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-base-content/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm">No hay mensajes nuevos</p>
            </div>
          ) : (
            <div className="mt-4 space-y-1">
              {d.recent_messages.slice(0, 5).map((msg) => (
                <Link
                  key={msg.id}
                  to="/app/messages"
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-base-200/50 transition-colors"
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                    msg.message_type === 'inquiry' ? 'bg-warning/15 text-warning' : 'bg-info/15 text-info'
                  }`}>
                    {msg.sender_name ? getInitials(msg.sender_name) : '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`badge badge-xs ${msg.message_type === 'inquiry' ? 'badge-warning' : 'badge-info'}`}>
                        {MSG_TYPE_LABELS[msg.message_type] || msg.message_type}
                      </span>
                    </div>
                    <p className="text-xs text-base-content/50 truncate mt-0.5">{msg.body_preview}</p>
                  </div>
                  <span className="text-xs text-base-content/30 whitespace-nowrap shrink-0">
                    {msg.created_at ? timeAgo(msg.created_at) : ''}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── End User Dashboard ─────────────────────────────────────────────────────

function EndUserDashboard({ data }: { data: Record<string, unknown> }): React.ReactElement {
  const d = data as {
    favorites_count: number;
    sent_inquiries: number;
    upcoming_visits: Array<{ id: number; property_id: number; scheduled_at: string; status: string }>;
  };

  const upcomingCount = d.upcoming_visits?.length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-medium text-base-content/40 uppercase tracking-widest">Mi panel</p>
        <h1 className="text-2xl font-bold mt-0.5">Bienvenido</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          }
          value={d.favorites_count}
          label="Favoritos"
          borderColor="border-l-warning"
          linkTo="/app/favorites"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          }
          value={d.sent_inquiries}
          label="Consultas enviadas"
          borderColor="border-l-primary"
        />
        <KpiCard
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
          value={upcomingCount}
          label="Próximas visitas"
          borderColor="border-l-info"
        />
      </div>

      {/* Upcoming Visits */}
      <div className="bg-base-100 shadow-sm border border-base-content/5 rounded-xl p-5">
        <SectionHeader title="Mis próximas visitas" />
        {upcomingCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-base-content/30">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm">No tienes visitas programadas</p>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {d.upcoming_visits.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between p-3 bg-base-200/30 rounded-lg hover:bg-base-200/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {new Date(v.scheduled_at).toLocaleDateString('es-AR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })}
                    </p>
                    <p className="text-xs text-base-content/40 font-mono">
                      {new Date(v.scheduled_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <span className={`badge badge-sm ${VISIT_STATUS_COLORS[v.status] || 'badge-ghost'}`}>
                  {VISIT_STATUS_LABELS[v.status] || v.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────

export default function DashboardHome(): React.ReactElement {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<{ data: Record<string, unknown> }>('/v1/analytics/dashboard'),
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton header */}
        <div>
          <div className="skeleton h-3 w-24 mb-2" />
          <div className="skeleton h-8 w-64" />
        </div>
        {/* Skeleton KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton h-28 rounded-xl" />
          ))}
        </div>
        {/* Skeleton charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 skeleton h-56 rounded-xl" />
          <div className="skeleton h-56 rounded-xl" />
        </div>
        {/* Skeleton bottom */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="skeleton h-64 rounded-xl" />
          <div className="skeleton h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!dashboardData?.data || !user) {
    return <p className="text-base-content/50">{t('welcome', { name: '' })}</p>;
  }

  const data = dashboardData.data;

  switch (user.role) {
    case 'root':
      return <RootDashboard data={data} />;
    case 'company_admin':
      return <CompanyAdminDashboard data={data as unknown as CompanyDashboard} />;
    case 'agent':
      return <AgentDashboard data={data} />;
    default:
      return <EndUserDashboard data={data} />;
  }
}
